import { useEffect } from 'react';
import { matchRoutes, useLocation } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';

interface RouteMeta {
  title: string;
  description: string;
  keywords?: string;
}

type MetaRouteObject = RouteObject & {
  meta: RouteMeta;
};

const metaRoutes: MetaRouteObject[] = [
  {
    path: '/',
    meta: {
      title: 'Home – ItsAcademic.ai',
      description: 'Dashboard for educators – ItsAcademic.ai',
      keywords: 'education, dashboard, analytics',
    },
  },
  {
    path: '/about',
    meta: {
      title: 'About – ItsAcademic.ai',
      description: 'Learn more about ItsAcademic.ai and our mission.',
      keywords: 'about, itsacademic, education',
    },
  },
  {
    path: '/pricing',
    meta: {
      title: 'Pricing – ItsAcademic.ai',
      description: 'Find the best plan for your educational needs.',
      keywords: 'pricing, plans, subscription',
    },
  },
  {
    path: '/attendance/:attendanceId',
    meta: {
      title: 'Attendance Details – ItsAcademic.ai',
      description: 'View attendance details for your course session.',
    },
  },
  {
    path: '/app/*',
    meta: {
      title: 'Dashboard – ItsAcademic.ai',
      description: 'Your dashboard for managing courses and students.',
    },
  },
];

declare global {
  interface Window {
    plausible?: (...args: any[]) => void;
  }
}

/**
 * MetaUpdater applies SEO, accessibility (Dublin Core), and social metadata,
 * and triggers Plausible pageview tracking on route changes.
 */
export default function MetaUpdater() {
  const location = useLocation();
  const matches = matchRoutes(metaRoutes as RouteObject[], location);
  const meta =
    matches && matches.length > 0
      ? (matches[matches.length - 1].route as MetaRouteObject).meta
      : metaRoutes[0].meta;

  useEffect(() => {
    if (window.plausible) {
      window.plausible('pageview', { u: window.location.pathname });
    }
  }, [location]);

  // Update document metadata imperatively – React 19 discourages
  // use of third-party head-managers like react-helmet.

  useEffect(() => {
    const url = window.location.href;

    const setMeta = (
      selector: { name?: string; property?: string },
      content: string,
    ) => {
      const metaEl = document.head.querySelector<HTMLMetaElement>(
        selector.name
          ? `meta[name="${selector.name}"]`
          : `meta[property="${selector.property}"]`,
      );
      if (metaEl) {
        metaEl.setAttribute('content', content);
      } else {
        const el = document.createElement('meta');
        if (selector.name) el.setAttribute('name', selector.name);
        if (selector.property) el.setAttribute('property', selector.property);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };

    document.title = meta.title;

    // Standard
    setMeta({ name: 'description' }, meta.description);
    if (meta.keywords) setMeta({ name: 'keywords' }, meta.keywords);

    // Dublin Core
    setMeta({ name: 'DC.title' }, meta.title);
    setMeta({ name: 'DC.description' }, meta.description);
    setMeta({ name: 'DC.publisher' }, 'ItsAcademic.ai');
    setMeta({ name: 'DC.language' }, 'en');
    setMeta({ name: 'DC.type' }, 'Text');
    setMeta({ name: 'DC.format' }, 'text/html');

    // Open Graph
    setMeta({ property: 'og:title' }, meta.title);
    setMeta({ property: 'og:description' }, meta.description);
    setMeta({ property: 'og:type' }, 'website');
    setMeta({ property: 'og:url' }, url);
    setMeta({ property: 'og:image' }, '/favicon.svg');

    // Twitter
    setMeta({ name: 'twitter:card' }, 'summary_large_image');
    setMeta({ name: 'twitter:title' }, meta.title);
    setMeta({ name: 'twitter:description' }, meta.description);
  }, [meta]);

  return null;
}